import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Mail, Lock, Chrome } from 'lucide-react';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: login/signup, 2: source selection
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sources, setSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/profile';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    // For now, just move to source selection
    setLoading(true);
    
    // Fetch sources
    try {
      const response = await fetch(`${BACKEND_URL}/api/sources`);
      const data = await response.json();
      setSources(data);
      setStep(2);
    } catch (error) {
      logError('AuthModal:fetchSources', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSource = (sourceId) => {
    setSelectedSources(prev => 
      prev.includes(sourceId)
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleComplete = () => {
    // Save user preferences
    const userData = {
      email,
      sources: selectedSources,
      loggedInAt: new Date().toISOString()
    };
    localStorage.setItem('user', JSON.stringify(userData));
    onClose();
    window.location.reload(); // Refresh to show personalized content
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="auth-modal">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-3xl font-serif font-bold text-center">
                Break Your Filter Bubble
              </DialogTitle>
              <p className="text-center text-gray-600 mt-2">
                Tell us what you read, and we'll show you what you're missing
              </p>
            </DialogHeader>

            <div className="space-y-4 mt-6">
              {/* Google Login */}
              <Button 
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full flex items-center justify-center gap-2 h-12"
                data-testid="google-login-button"
              >
                <Chrome className="w-5 h-5" />
                Continue with Google
              </Button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Email Login Form */}
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12"
                  disabled={loading}
                  data-testid="continue-button"
                >
                  {loading ? 'Loading...' : 'Continue'}
                </Button>
              </form>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-3xl font-serif font-bold">
                What do you usually read?
              </DialogTitle>
              <p className="text-gray-600 mt-2">
                Select the news sources you regularly follow. We'll use this to show you different perspectives.
              </p>
            </DialogHeader>

            <div className="mt-6">
              <p className="text-sm text-gray-600 mb-4">
                {selectedSources.length} of {sources.length} sources selected
              </p>

              <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto pr-2">
                {sources.map((source) => (
                  <div
                    key={source.id}
                    className="flex items-start space-x-3 p-3 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => toggleSource(source.id)}
                    data-testid={`source-checkbox-${source.id}`}
                  >
                    <Checkbox
                      checked={selectedSources.includes(source.id)}
                      onCheckedChange={() => toggleSource(source.id)}
                    />
                    <label className="font-medium text-sm cursor-pointer flex-1">
                      {source.name}
                    </label>
                  </div>
                ))}
              </div>

              <div className="flex justify-between mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button 
                  onClick={handleComplete}
                  disabled={selectedSources.length === 0}
                  data-testid="complete-button"
                >
                  Complete Setup ({selectedSources.length} sources)
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
